import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Gallery } from '../components/Gallery';
import { VideoSection } from '../components/VideoSection';
import {
  COMPANY_INFO,
  USPS,
  CLICK_STEPS,
  SITE_IMAGES,
  getCdnImage,
} from '../data/content';

export function Home() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      userType: formData.get('userType'),
      message: formData.get('message'),
      website_url: formData.get('website_url'),
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormSubmitted(true);
      } else {
        setFormSubmitted(true);
      }
    } catch (error) {
      setFormSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Forside"
        description="AGT Nordic Group ApS er dansk eneforhandler af SYSTEM 3E. Oplev det innovative modulære vægsystem med Click-Click samling uden mørtel eller lim."
      />

      {/* Hero Section - Centered Content */}
      <section className="relative bg-[#243447] text-white overflow-hidden pt-28 pb-16 sm:pt-40 sm:pb-20 md:pt-52 md:pb-24 lg:pt-64 lg:pb-28 lg:min-h-[85vh] flex items-end">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none">
          <img
            src={getCdnImage(SITE_IMAGES.hero, 1920, 85)}
            srcSet={`
              ${getCdnImage(SITE_IMAGES.hero, 800, 80)} 800w,
              ${getCdnImage(SITE_IMAGES.hero, 1400, 85)} 1400w,
              ${getCdnImage(SITE_IMAGES.hero, 1920, 85)} 1920w
            `}
            sizes="100vw"
            alt="SYSTEM 3E - Byg vægge på en smartere og hurtigere måde"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center w-full">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 [text-shadow:_0_2px_12px_rgba(0,0,0,0.85),_0_4px_24px_rgba(0,0,0,0.7)]">
              Byg vægge på en <span className="text-[#68a0f0] [text-shadow:_0_2px_12px_rgba(0,0,0,0.9)]">smartere</span> og <span className="text-[#68a0f0] [text-shadow:_0_2px_12px_rgba(0,0,0,0.9)]">hurtigere</span> måde
            </h1>

            <p className="text-base sm:text-xl text-white leading-relaxed font-medium mb-8 max-w-2xl mx-auto [text-shadow:_0_2px_8px_rgba(0,0,0,0.85),_0_4px_16px_rgba(0,0,0,0.6)]">
              SYSTEM 3E er et modulbaseret perlit-vægsystem med patenteret <strong className="text-white font-semibold">Click-Click-samling</strong>. Opfør stærke, isolerende og brandsikre vægge uden mørtel, lim eller vand.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink
                to="/sadan-bygger-du"
                className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-center text-base transition-all shadow-xl flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <span>Se hvordan det virker</span>
                <i className="fa-solid fa-circle-play" aria-hidden="true" />
              </NavLink>

              <NavLink
                to="/kontakt"
                className="bg-[#25282a]/90 hover:bg-[#122a4e] text-white font-bold px-8 py-4 rounded-xl text-center text-base transition-all border border-[#446ba9]/40 flex items-center justify-center gap-2 w-full sm:w-auto shadow-xl"
              >
                <span>Tal med os om dit projekt</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Hurtige USP'er - Split Layout with Overlapping Element */}
      <section className="py-16 sm:py-24 bg-[#F4F5FA] border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#5A6E75]/10 text-[#5A6E75] border border-[#5A6E75]/30 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                <span>Hvorfor vælge SYSTEM 3E?</span>
              </div>

              <h2 className="text-3xl sm:text-5xl font-black text-[#0D0D0D] tracking-tight leading-tight">
                Danmarks Mest Moderne Vægkonstruktion
              </h2>

              <p className="text-[#5A6E75] text-base leading-relaxed">
                SYSTEM 3E forener bærende konstruktion, varmeisolering og uovertruffen brandmodstand i ét samlet modulmønster uden brug af mørtel, lim eller vand.
              </p>

              {/* Stat Figure */}
              <div className="py-4 border-y border-[#5A6E75]/20 flex items-baseline gap-4">
                <span className="text-4xl sm:text-5xl font-black text-[#5A6E75]">4,5 min/m²</span>
                <span className="text-xs sm:text-sm font-semibold text-[#0D0D0D] max-w-xs">
                  Lynhurtig opførelse af råhusvægge uden tørretid
                </span>
              </div>

              <div>
                <NavLink
                  to="/fordele"
                  className="inline-flex items-center gap-2 bg-[#5A6E75] hover:bg-[#0D0D0D] text-white font-bold px-7 py-4 rounded-xl text-sm transition-all shadow-md"
                >
                  <span>Udforsk alle fordele</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </NavLink>
              </div>
            </div>

            {/* Right Column with Image and Overlapping Card */}
            <div className="lg:col-span-6 relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#0D0D0D]">
                <img
                  src={getCdnImage(SITE_IMAGES.projectVillaVordingborg, 1000, 85)}
                  alt="SYSTEM 3E Perlit konstruktion"
                  className="w-full h-[380px] sm:h-[480px] object-cover"
                />
              </div>

              {/* Overlapping Feature Card */}
              <div className="lg:absolute lg:-bottom-10 lg:-left-12 mt-6 lg:mt-0 z-20 bg-white border border-[#5A6E75]/20 rounded-2xl p-6 shadow-xl max-w-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <div className="w-10 h-10 bg-[#5A6E75]/10 text-[#5A6E75] rounded-lg flex items-center justify-center text-lg mb-2">
                      <i className="fa-solid fa-droplet-slash" aria-hidden="true" />
                    </div>
                    <h4 className="font-bold text-[#0D0D0D] text-sm">100% Tør Montage</h4>
                    <p className="text-xs text-[#5A6E75] leading-relaxed">
                      Låser mekanisk. Byg uanset årstid uden tørretid.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="w-10 h-10 bg-[#5A6E75]/10 text-[#5A6E75] rounded-lg flex items-center justify-center text-lg mb-2">
                      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                    </div>
                    <h4 className="font-bold text-[#0D0D0D] text-sm">REI 240 + M</h4>
                    <p className="text-xs text-[#5A6E75] leading-relaxed">
                      Højeste brandklasse og fremragende lydisolering.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hvad er SYSTEM 3E? - Refined Editorial Split Layout */}
      <section className="py-16 sm:py-24 bg-[#F2F3F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Asymmetrical Staggered Images */}
            <div className="lg:col-span-6 flex items-start gap-4 sm:gap-6">
              <div className="w-[48%] h-[320px] sm:h-[420px]">
                <img
                  src={getCdnImage(SITE_IMAGES.elementsCloseUp, 800, 85)}
                  alt="SYSTEM 3E perlitelementer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-[52%] h-[360px] sm:h-[460px] mt-8 sm:mt-12">
                <img
                  src={getCdnImage(SITE_IMAGES.modularL01, 800, 85)}
                  alt="SYSTEM 3E Modulært Vægsystem"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Column: Compact Editorial Text Block */}
            <div className="lg:col-span-6 space-y-6">
              <div className="flex items-center gap-3">
                <span className="w-8 h-[2px] bg-[#5A6E75]" />
                <span className="text-[#5A6E75] font-bold text-xs uppercase tracking-[0.2em]">
                  Enkel Byggemetode
                </span>
              </div>

              <h2 className="text-3xl sm:text-5xl font-black text-[#0D0D0D] tracking-tight leading-tight">
                Hvad er SYSTEM 3E?
              </h2>

              <p className="text-lg sm:text-xl font-medium text-[#0D0D0D] leading-relaxed">
                SYSTEM 3E er et revolutionerende modulbaseret vægsystem skabt af præcist udskårne elementer, fremstillet af det naturlige mineral perlit.
              </p>

              <p className="text-[#5A6E75] text-sm sm:text-base leading-relaxed">
                Hovedprincippet er at gøre opbygningen af råhuset så enkel og hurtig som at samle modulære byggeklodser – men med fuld bæreevne, ekstraordinær isolering og højeste brandmodstand uden brug af våd mørtel eller lim.
              </p>

              <div className="pt-2">
                <NavLink
                  to="/system-3e"
                  className="inline-flex items-center gap-2 text-[#0D0D0D] font-bold text-sm hover:text-[#5A6E75] transition-colors border-b-2 border-[#0D0D0D] pb-1"
                >
                  <span>Læs alle produktspecifikationer</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Click-Click demonstration - Counter Layout */}
      <section className="py-16 sm:py-24 bg-[#F4F5FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Centered Header Block */}
          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-5xl font-black text-[#0D0D0D] tracking-tight mb-4">
              Click-Click Princip i 3 Enkle Trin
            </h2>
            <p className="text-[#5A6E75] text-base sm:text-lg max-w-xl mx-auto">
              Ingen tørretid, ingen mørtelblandere – blot hurtig, ren og præcis samling.
            </p>
          </div>

          {/* 3 Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {CLICK_STEPS.map((stepItem, idx) => (
              <div key={idx} className="flex flex-col items-center text-center">
                {/* Circular Focal Element */}
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#5A6E75] text-[#F4F5FA] flex items-center justify-center font-black text-2xl sm:text-3xl mb-6">
                  {idx + 1}
                </div>

                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5A6E75] mb-2">
                  {stepItem.subtitle}
                </span>

                <h3 className="text-xl sm:text-2xl font-black text-[#0D0D0D] mb-3">
                  {stepItem.title}
                </h3>

                <p className="text-[#5A6E75] text-sm leading-relaxed max-w-xs">
                  {stepItem.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection />

      {/* Private vs. Professionelle Split Section */}
      <section className="py-16 sm:py-24 bg-[#25282a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#446ba9] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Tilpasset Din Situation
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
              Uanset om du er privatbygherre eller fagmand
            </h2>
            <p className="text-slate-300 text-base mt-3">
              AGT Nordic Group ApS rådgiver og leverer SYSTEM 3E løsninger til både private drømmehuse og professionelle byggeprojekter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Private Box */}
            <div className="bg-[#181818] border border-slate-700 rounded-3xl p-8 sm:p-10 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-[#122a4e]/40 text-[#446ba9] rounded-2xl flex items-center justify-center text-2xl mb-6">
                  <i className="fa-solid fa-house-chimney" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-black text-white mb-3">
                  Skal du bygge dit eget hus?
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Få et sundt indeklima, ekstremt lave energiudgifter og en hurtigere byggeproces. Vi guider dig trygt igennem fra idé til levering af elementerne.
                </p>

                <ul className="space-y-2.5 text-xs text-slate-300 mb-8">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Letforståeligt koncept uden unødig ingeniørjargon</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Perlit giver høj termisk komfort og lydisolering</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Mulighed for at lade din egen håndværker udføre montagen</span>
                  </li>
                </ul>
              </div>

              <NavLink
                to="/for-private"
                className="w-full text-center bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold py-3.5 px-6 rounded-xl text-sm transition-all shadow"
              >
                Læs om fordele for private bygherrer
              </NavLink>
            </div>

            {/* Professionelle Box */}
            <div className="bg-[#181818] border border-slate-700 rounded-3xl p-8 sm:p-10 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-[#122a4e]/40 text-[#446ba9] rounded-2xl flex items-center justify-center text-2xl mb-6">
                  <i className="fa-solid fa-helmet-safety" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-black text-white mb-3">
                  Arbejder du professionelt med byggeri?
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Optimér din byggepladslogistik, reducér tidsforbruget med op til 70% og skån dine håndværkere for tunge våde løft. Full-service support fra den danske eneforhandler.
                </p>

                <ul className="space-y-2.5 text-xs text-slate-300 mb-8">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Ren tør byggemetode – ingen fugtfælder på pladsen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Op til 100 m² ydervæg opført på et døgn</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#446ba9]" aria-hidden="true" />
                    <span>Præcis projektberegning og minimalt materialespild</span>
                  </li>
                </ul>
              </div>

              <NavLink
                to="/for-professionelle"
                className="w-full text-center bg-slate-100 hover:bg-white text-slate-900 font-bold py-3.5 px-6 rounded-xl text-sm transition-all shadow"
              >
                Se løsninger til professionelle
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Projekter / Inspirationsgalleri */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-[#5A6E75] font-extrabold text-xs uppercase tracking-widest block mb-2">
                Inspiration & Referencer
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0D0D0D] tracking-tight">
                Udvalgte SYSTEM 3E Projekter
              </h2>
            </div>
            <NavLink
              to="/projekter"
              className="inline-flex items-center gap-2 text-[#5A6E75] font-bold text-sm hover:text-[#0D0D0D] transition-colors"
            >
              <span>Se alle referencer og gallerier</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>
          </div>

          <Gallery gallerySlug="projekter" />
        </div>
      </section>

      {/* Eneforhandler Contact & Webform Section - Webform on Right */}
      <section className="relative py-16 sm:py-24 bg-[#25282a] text-white overflow-hidden">
        {/* Background Image with Cloudflare optimization */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6db9bc06-2d1f-414d-89eb-eaf682030584/imgi-2-calculator-header.jpg')`
          }}
        />
        {/* Clean Dark Overlay */}
        <div className="absolute inset-0 bg-[#25282a]/90" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Heading & Contact Info */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#446ba9] block">
                Officiel Eneforhandler i Danmark
              </span>

              <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight text-white">
                Klar til at starte dit byggeprojekt med SYSTEM 3E?
              </h2>

              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                Kontakt <strong className="text-white font-bold">{COMPANY_INFO.name}</strong> i Vordingborg. Vi rådgiver om beregning, levering og montage over hele Danmark for både private og erhverv.
              </p>

              <div className="pt-2 space-y-2 text-sm text-slate-200 font-medium">
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#446ba9]" aria-hidden="true" />
                  <span>{COMPANY_INFO.address}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#446ba9]" aria-hidden="true" />
                  <a href={COMPANY_INFO.phoneRaw} className="hover:underline">{COMPANY_INFO.phone}</a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-[#446ba9]" aria-hidden="true" />
                  <a href={`mailto:${COMPANY_INFO.email}`} className="hover:underline">{COMPANY_INFO.email}</a>
                </p>
              </div>

              <div className="pt-4">
                <a
                  href={COMPANY_INFO.phoneRaw}
                  className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-6 py-3.5 rounded-full text-sm hover:bg-white hover:text-[#0D0D0D] transition-all"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  <span>Ring direkte: {COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-6 bg-transparent border border-white/20 rounded-3xl p-8 shadow-xl backdrop-blur-xs">
              <h3 className="text-2xl font-black text-white mb-6">Send en forespørgsel</h3>

              {formSubmitted ? (
                <div className="bg-emerald-900/40 border border-emerald-500/50 text-emerald-200 p-6 rounded-2xl text-center space-y-2">
                  <i className="fa-solid fa-circle-check text-3xl text-emerald-400 mb-2" aria-hidden="true" />
                  <h4 className="font-bold text-lg text-white">Tak for din henvendelse!</h4>
                  <p className="text-sm">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Antispam Honeypot */}
                  <input
                    type="text"
                    name="website_url"
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Dit fulde navn"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/40 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="din@email.dk"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/40 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="Dit tlf.nr"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/40 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                        Jeg er
                      </label>
                      <select
                        name="userType"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white outline-none transition-colors [&>option]:bg-[#0D0D0D] [&>option]:text-white"
                      >
                        <option value="privat">Privat bygherre</option>
                        <option value="haandvaerker">Håndværker / Entreprenør</option>
                        <option value="arkitekt">Arkitekt / Rådgiver</option>
                        <option value="anden">Andet</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                      Beskriv dit projekt / forespørgsel *
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      required
                      placeholder="Fortæl kort om byggeprojektet (m², beliggenhed, tidshorisont)..."
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/40 outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold py-4 rounded-full text-sm transition-all shadow-md cursor-pointer disabled:opacity-50 border border-white/20"
                  >
                    {loading ? 'Sender...' : 'Send forespørgsel'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
